import React from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiUser,FiBookOpen,FiList,FiEdit3,FiPenTool,FiInfo}=FiIcons;

const TemplateEditor=({formData,onChange})=> {
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 space-y-8">
      <div className="flex items-center border-b border-gray-100 pb-4">
        <SafeIcon icon={FiEdit3} className="w-6 h-6 mr-3" style={{color: '#4a8ccc'}} />
        <h2 className="text-xl font-semibold text-gray-800">Lesson Details</h2>
      </div>

      {/* Section 1: Student Details */}
      <section className="space-y-4">
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Student Info</h3>
        <div className="space-y-2">
          <label className="flex items-center text-sm font-medium text-gray-700">
            <SafeIcon icon={FiUser} className="w-4 h-4 mr-2" style={{color: '#4a8ccc'}} />
            Student / Parent Name
          </label>
          <input 
            type="text" 
            value={formData.student_name} 
            onChange={(e)=> onChange('student_name',e.target.value)} 
            placeholder="e.g. Alex" 
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:border-blue-500 outline-none transition-all"
            style={{focusRingColor: '#4a8ccc', borderColor: '#4a8ccc'}}
          />
        </div>
      </section>

      {/* Section 2: Lesson Content */}
      <section className="space-y-4">
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Lesson Content</h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700">
              <SafeIcon icon={FiBookOpen} className="w-4 h-4 mr-2" style={{color: '#4a8ccc'}} />
              Topics Covered
            </label>
            <input 
              type="text" 
              value={formData.topic} 
              onChange={(e)=> onChange('topic',e.target.value)} 
              placeholder="e.g. Past Tense Verbs" 
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:border-blue-500 outline-none transition-all"
              style={{focusRingColor: '#4a8ccc', borderColor: '#4a8ccc'}}
            />
          </div>
          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700">
              <SafeIcon icon={FiList} className="w-4 h-4 mr-2" style={{color: '#4a8ccc'}} />
              New Words / Learning Points
            </label>
            <div className="relative">
              <textarea 
                value={formData.vocab_grammar} 
                onChange={(e)=> onChange('vocab_grammar',e.target.value)} 
                placeholder="Enter each word/grammar point on a new line&#10;e.g.&#10;walked&#10;talked&#10;played&#10;&#10;Verb: like" 
                rows={6} 
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:border-blue-500 outline-none transition-all resize-none font-mono text-sm"
                style={{focusRingColor: '#4a8ccc', borderColor: '#4a8ccc'}}
              />
              <div className="absolute top-2 right-2">
                <div className="group relative">
                  <SafeIcon icon={FiInfo} className="w-4 h-4 text-gray-400 cursor-help" />
                  <div className="absolute right-0 top-6 w-64 p-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                    Each line will become a checked item in the final message
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Performance */}
      <section className="space-y-4">
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Performance</h3>
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-100 space-y-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <span className="text-gray-700 font-medium whitespace-nowrap">You did</span>
            <select 
              value={formData.performance_level} 
              onChange={(e)=> onChange('performance_level',e.target.value)} 
              className="px-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 outline-none cursor-pointer"
              style={{focusRingColor: '#4a8ccc', borderColor: '#4a8ccc'}}
            >
              <option value="really well">really well</option>
              <option value="well">well</option>
            </select>
            <span className="text-gray-700 font-medium whitespace-nowrap">at</span>
          </div>
          <div className="w-full">
            <input 
              type="text" 
              value={formData.performance_content} 
              onChange={(e)=> onChange('performance_content',e.target.value)} 
              placeholder="e.g. pronunciation and fluency" 
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:border-blue-500 outline-none transition-all"
              style={{focusRingColor: '#4a8ccc', borderColor: '#4a8ccc'}}
            />
          </div>
        </div>
      </section>

      {/* Section 4: Future Improvements */}
      <section className="space-y-4">
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Future Improvements</h3>
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-gray-600 mb-3">Next time we will continue to work on...</p>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-500 uppercase">Focus Area 1</label>
              <input 
                type="text" 
                value={formData.improvement_1} 
                onChange={(e)=> onChange('improvement_1',e.target.value)} 
                placeholder="e.g. reading speed" 
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:border-blue-500 outline-none transition-all"
                style={{focusRingColor: '#4a8ccc', borderColor: '#4a8ccc'}}
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-500 uppercase">Focus Area 2</label>
              <input 
                type="text" 
                value={formData.improvement_2} 
                onChange={(e)=> onChange('improvement_2',e.target.value)} 
                placeholder="e.g. vowel sounds" 
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:border-blue-500 outline-none transition-all"
                style={{focusRingColor: '#4a8ccc', borderColor: '#4a8ccc'}}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Notes */}
      <section className="space-y-4">
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Final Note</h3>
        <div className="space-y-3">
          <select 
            value={formData.note_type} 
            onChange={(e)=> onChange('note_type',e.target.value)} 
            className="w-full md:w-auto px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 outline-none cursor-pointer text-sm font-medium text-gray-700"
            style={{focusRingColor: '#4a8ccc', borderColor: '#4a8ccc'}}
          >
            <option value="Homework Assignment">Homework Assignment</option>
            <option value="Teacher Note">Teacher Note</option>
            <option value="Parent Update">Parent Update</option>
            <option value="Practice Reminder">Practice Reminder</option>
          </select>
          <textarea 
            value={formData.note_content} 
            onChange={(e)=> onChange('note_content',e.target.value)} 
            placeholder="Write your note here..." 
            rows={3} 
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:border-blue-500 outline-none transition-all resize-none"
            style={{focusRingColor: '#4a8ccc', borderColor: '#4a8ccc'}}
          />
        </div>
      </section>

      {/* Section 6: Signature */}
      <section className="space-y-4">
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Signature</h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700">
              <SafeIcon icon={FiPenTool} className="w-4 h-4 mr-2" style={{color: '#4a8ccc'}} />
              Title
            </label>
            <input 
              type="text" 
              value={formData.signature_title} 
              onChange={(e)=> onChange('signature_title',e.target.value)} 
              placeholder="e.g. Best regards," 
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:border-blue-500 outline-none transition-all"
              style={{focusRingColor: '#4a8ccc', borderColor: '#4a8ccc'}}
            />
          </div>
          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700">
              <SafeIcon icon={FiUser} className="w-4 h-4 mr-2" style={{color: '#4a8ccc'}} />
              Your Name
            </label>
            <input 
              type="text" 
              value={formData.signature_name} 
              onChange={(e)=> onChange('signature_name',e.target.value)} 
              placeholder="e.g. Your Teacher" 
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:border-blue-500 outline-none transition-all"
              style={{focusRingColor: '#4a8ccc', borderColor: '#4a8ccc'}}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default TemplateEditor;